import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import axios from "../../utils/axiosInterceptor";
import { BASE_URL } from "../../Constant";

const CACHE_KEY = "tdc_seo_settings";
const CACHE_TTL = 30 * 60 * 1000; // 30 minutes

function loadCached() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const { data, ts } = JSON.parse(raw);
    if (Date.now() - ts > CACHE_TTL) return null;
    return data;
  } catch {
    return null;
  }
}

function saveCache(data) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({ data, ts: Date.now() }));
  } catch {}
}

function applyMeta(noindex, noimageindex) {
  let tag = document.querySelector('meta[name="robots"]');
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute("name", "robots");
    document.head.appendChild(tag);
  }

  const directives = [];
  if (noindex) directives.push("noindex", "nofollow");
  else directives.push("index", "follow");
  if (noimageindex) directives.push("noimageindex");

  tag.setAttribute("content", directives.join(", "));
}

export default function SeoMetaManager() {
  const location = useLocation();
  const settingsRef = useRef(null);

  useEffect(() => {
    const cached = loadCached();
    if (cached) {
      settingsRef.current = cached;
      return;
    }
    axios.get(`${BASE_URL}/seo/settings`)
      .then((res) => {
        if (res.data?.menus) {
          settingsRef.current = res.data.menus;
          saveCache(res.data.menus);
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const menus = settingsRef.current;
    if (!menus) return;

    const state = location.state || {};
    const pathname = location.pathname;

    // Check branded jewellery — detected via location.state.brandName
    const branded = menus.find((m) => m.key === "branded_jewellery");
    const isBrandedPage = !!state.brandName;

    // Check path-based matches for any noindex menu
    const pathNoindex = menus.some(
      (m) => m.noindex && m.disallow_paths.some((p) => pathname.startsWith(p))
    );
    const pathNoimageindex = menus.some(
      (m) => m.noimageindex && m.disallow_paths.some((p) => pathname.startsWith(p))
    );

    const shouldNoindex = pathNoindex || (isBrandedPage && branded?.noindex);
    const shouldNoimageindex = pathNoimageindex || (isBrandedPage && branded?.noimageindex);

    applyMeta(shouldNoindex, shouldNoimageindex);
  }, [location]);

  return null;
}
